import { create } from 'zustand'

import { INBOX_PAGE, INBOX_STALE_MS, dataReview, dataReviewKeys } from '@/services/dataReview'

/**
 * Which answer of the desk the Data Intelligence Review inbox is showing: how
 * many records, and whether the found datapoints no live deal backs are listed.
 *
 * The Intake tab badges from the inbox's own read. When the inbox asked for a
 * longer page, or for the unbacked rows, on a key of its own, the badge kept
 * the first page's key active beside it — and every Add, dismissal or bulk
 * dismissal then computed the whole desk twice, the API's heaviest read, once
 * for a count the other answer already held: the totals are the desk's either
 * way. The badge and the inbox now both read the view held here, so the desk
 * is read once, with whatever the inbox asked for, and a decision refreshes
 * that one read.
 *
 * The inbox resets it when it closes, so it opens on the first page with the
 * unbacked rows left off, as it always has.
 */
interface DataReviewInboxView {
  limit: number
  includeUnbacked: boolean
  setLimit: (limit: number) => void
  setIncludeUnbacked: (includeUnbacked: boolean) => void
  reset: () => void
}

export const useDataReviewInboxView = create<DataReviewInboxView>((set) => ({
  limit: INBOX_PAGE,
  includeUnbacked: false,
  setLimit: (limit) => set({ limit }),
  setIncludeUnbacked: (includeUnbacked) => set({ includeUnbacked }),
  reset: () => set({ limit: INBOX_PAGE, includeUnbacked: false }),
}))

/**
 * The inbox's read for a view: its key and its call — a page of `limit`
 * records, with or without the unbacked found datapoints. The Intake badge and
 * the inbox both read it with the view above, so they share one answer.
 */
export function inboxQuery({ limit = INBOX_PAGE, includeUnbacked = false }: {
  limit?: number
  includeUnbacked?: boolean
} = {}) {
  return {
    queryKey: dataReviewKeys.inboxFor(limit, includeUnbacked),
    queryFn: () => dataReview.inbox({
      ...(limit === INBOX_PAGE ? {} : { limit }),
      ...(includeUnbacked ? { includeUnbacked: true } : {}),
    }),
    staleTime: INBOX_STALE_MS,
  }
}
